import { create_input_type_channel_config, V3InputTypeChannelConfig } from './inverter.config';
import { CustomInverterType, DataType, InverterData, InverterDataComposer, ReadWrite, RegisterTable } from '../lib/inverter.model';
import { getCountryNameFromCodeV3 } from './country-code';
import { modbusServicesLabels } from '../modbus-services.services';

export const V3VersionInfo: Partial<RegisterTable> = {
  SN: {
    address: 0x8020,
    name: 'Serial_Number0-7',
    dataType: DataType.ASCII,
    description: 'Serial number',
    access: ReadWrite.Read,
    quantity: 10,
  },
  ARM: {
    address: 0x044F,
    name: 'arm',
    dataType: DataType.ASCII,
    description: 'Communication CPU Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  HARDWARE_VERSION: {
    address: 0x044D,
    name: 'hardware_version',
    dataType: DataType.ASCII,
    description: 'Hardware Version',
    access: ReadWrite.Read,
    quantity: 2,
  },
  MAIN_VERSION: {
    address: 0x045B,
    name: 'main_safety_version',
    dataType: DataType.ASCII,
    description: 'Main Safety Version',
    access: ReadWrite.Read,
    quantity: 2,
  },
  DSPM: {
    address: 0x044F + 0x0004,
    name: 'dspm',
    dataType: DataType.ASCII,
    description: 'Master Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  DSPS: {
    address: 0x044F + 0x0008,
    name: 'dsps',
    dataType: DataType.ASCII,
    description: 'Vice Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  SAFETY_FIRMWARE_VERSION: {
    address: 0x0460,
    name: 'safety_firmware_version',
    dataType: DataType.ASCII,
    description: 'Safety Firmware Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  MODBUS_PROTOCOL_VERSION: {
    address: 0x0044,
    name: 'MODBUS_PROTOCOL_VERSION',
    dataType: DataType.BCD16,
    description: 'Modbus Protocol Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  SAFETY_VERSION: {
    address: 0x2005,
    name: 'SAFETY_VERSION',
    dataType: DataType.U16,
    description: 'Safety Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3DeviceCountry: Partial<RegisterTable> = {
  COUNTRY: {
    address: 0x2004,
    name: 'COUNTRY',
    dataType: DataType.U16,
    description: 'Country',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceInputModeConfig: Partial<RegisterTable> = {
  INPUT_MODE_CONFIG: {
    address: 0x1010,
    name: 'INPUT_MODE_CONFIG',
    dataType: DataType.U16,
    description: 'Input Mode Config',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  EPS_CONTROL: {
    address: 0x1029,
    name: 'EPS_CONTROL',
    dataType: DataType.U16,
    description: 'EPS Control',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  EPS_WAIT_TIME: {
    address: 0x102A,
    name: 'EPS_WAIT_TIME',
    dataType: DataType.U16,
    description: 'EPS Wait Time',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  SCAN_CURVE_IV_CONTROL: {
    address: 0x1025,
    name: 'SCAN_CURVE_IV',
    dataType: DataType.U16,
    description: 'Scan Curve IV',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  SCAN_CURVE_IV_PERIOD: {
    address: 0x1026,
    name: 'SCAN_CURVE_IV_PERIOD',
    dataType: DataType.U16,
    description: 'Scan Curve IV Period',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  ANTI_REFLUX_CONTROL: {
    address: 0x1023,
    name: 'ANTI_REFLUX_CONTROL',
    dataType: DataType.U16,
    description: 'Anti reflux control',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  ANTI_REFLUX_POWER: {
    address: 0x1024,
    name: 'ANTI_REFLUX_POWER',
    dataType: DataType.U16,
    description: 'Anti reflux power',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    accuracy: 100,
    unit: 'W',
  },
  PARALLEL_CONTROL: {
    address: 0x1035,
    name: 'PARALLEL_CONTROL',
    dataType: DataType.U16,
    description: `0: Disable the inverters' parallel function 1: Enable the inverters' parallel function`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  PARALLEL_MASTER_SLAVE: {
    address: 0x1036,
    name: 'PARALLEL_MASTER_SLAVE',
    dataType: DataType.U16,
    description: `0: Slave 1: Master`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  PARALLEL_ADDRESS: {
    address: 0x1037,
    name: 'PARALLEL_ADDRESS',
    dataType: DataType.U16,
    description: 'Parallel Address',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  UNBALANCED_SUPPORT_CONTROL: {
    address: 0x1038,
    name: 'UNBALANCED_SUPPORT_CONTROL',
    dataType: DataType.U16,
    description: `0: Disable the inverters' unbalanced support function 1: Enable the inverters' unbalanced support function`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  BATTERY_ACTIVE_CONTROL: {
    address: 0x102B,
    name: 'BATTERY_ACTIVE_CONTROL',
    dataType: DataType.U16,
    description: 'Battery Active Control',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  BATTERY_ACTIVE_ONESHOT: {
    address: 0x102C,
    name: 'BATTERY_ACTIVE_ONESHOT',
    dataType: DataType.U16,
    description: `Battery active input value 1, enable the battery active one time.
    It will return to pervious operation status when you read it.
    0x0000：success
    0x0001：Operating
    0xFFFB：Fail,controller refuse to response (controller busy or configuration issue）
    0xFFFC：Fail ,Controller no response
    0xFFFD：Fail, Current Function Disable
    0xFFFE：Fail,Parameter can not be save
    0xFFFF：Fail Input Parameters incorrect`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceInputTypeChannelConfig: Partial<RegisterTable> = {
  AddressMask_Config_Basic1: {
    address: 0x1000,
    name: 'AddressMask_Config_Basic1',
    dataType: DataType.U64,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'AddressMask_Config_Basic1',
    quantity: 4,
    isMask: true,
  },
};

const Input_Type_Channel_Config_Start = V3InputTypeChannelConfig.Input_Type.start;
for (let i = 0; i <= V3InputTypeChannelConfig.Input_Type.length; i++) {
  const { input_type } = create_input_type_channel_config(
    i,
    Input_Type_Channel_Config_Start + i,
  );
  V3DeviceInputTypeChannelConfig[`INPUT_TYPE_CHANNEL_CONFIG${i}`] = input_type;
}

export const V3DeviceLogicalInterface: Partial<RegisterTable> = {
  REMOTE_CONFIG: {
    address: 0x0900,
    name: 'REMOTE_CONFIG',
    dataType: DataType.U16,
    description: 'Remote Config',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceBatteryConfig: Partial<RegisterTable> = {
  BatConfig_ID: {
    address: 0x1044,
    name: 'Battery configuration ID',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration ID [0-7]',
    quantity: 1,
  },
  BatConfig_Address: {
    address: 0x1045,
    name: 'Battery configuration address',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration address [0-99]',
    quantity: 1,
  },
  BatConfig_Protocol: {
    address: 0x1046,
    name: 'Battery configuration protocol',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration protocol [0-65535]',
    quantity: 1,
  },
  BatConfig_Voltage_Over: {
    address: 0x1047,
    name: 'Battery configuration overvoltage protection',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration overvoltage protection',
    quantity: 1,
  },
  BatConfig_Voltage_Charge: {
    address: 0x1048,
    name: 'Battery configuration charge voltage protection',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration charge voltage protection',
    quantity: 1,
  },
  BatConfig_Voltage_Lack: {
    address: 0x1049,
    name: 'Battery configuration undervoltage protection',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration undervoltage protection',
    quantity: 1,
  },
  BatConfig_Voltage_Discharge_Stop: {
    address: 0x104a,
    name: 'Battery configuration lowest discharge voltage',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration lowest discharge voltage',
    quantity: 1,
  },
  BatConfig_Current_Charge_Limit: {
    address: 0x104b,
    name: 'Battery configuration maximum charge current limit',
    dataType: DataType.U16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration maximum charge current limit',
    quantity: 1,
  },
  BatConfig_Current_Discharge_Limit: {
    address: 0x104c,
    name: 'Battery configuration maximum discharge current limit',
    dataType: DataType.U16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration maximum discharge current limit',
    quantity: 1,
  },
  BatConfig_Depth_of_Discharge: {
    address: 0x104d,
    name: 'Battery configuration maximum discharge power',
    dataType: DataType.U16,
    unit: '%',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description:
      'Battery configuration maximum discharge power, when SOC<1-DOD, inverter will stop power discharge',
    quantity: 1,
  },
  BatConfig_End_of_Discharge: {
    address: 0x104e,
    name: 'Battery configuration maximum discharge power on off grid mode',
    dataType: DataType.U16,
    unit: '%',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description:
      'Battery configuration maximum discharge power on off grid mode, when SOC<1-EOD, inverter will stop power discharge',
    quantity: 1,
  },
  BatConfig_Capacity: {
    address: 0x104f,
    name: 'Battery configuration capacity',
    dataType: DataType.U16,
    unit: 'Ah',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration capacity',
    quantity: 1,
  },
  BatConfig_Voltage_Nominal: {
    address: 0x1050,
    name: 'Battery configuration rated battery voltage',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration rated battery voltage',
    quantity: 1,
  },
  BatConfig_Cell_Type: {
    address: 0x1051,
    name: 'Battery configuration battery type',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: `Battery configuration battery type
    0: Lead-acid(Default)
    1: lithium iron phosphate Battery
    2: Lithium Ternary Battery
    3: lithium titanate Battery
    4：AGM
    5：Gel
    6：Flooded`,
    quantity: 1,
  },
  BatConfig_EPS_Buffer: {
    address: 0x1052,
    name: 'Battery configuration off-grid recovery discharge hysteresis',
    dataType: DataType.U16,
    unit: '%',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration off-grid recovery discharge hysteresis',
    quantity: 1,
  },
};

export const V3DeviceResistanceConfig: Partial<RegisterTable> = {
  ISO_CONFIG: {
    address: 0x0A02,
    name: 'ISO_CONFIG',
    dataType: DataType.U16,
    description: 'ISO Config',
    access: ReadWrite.Read,
    quantity: 1,
  },
  INSULATION_RESISTANCE: {
    address: 0x042B,
    name: 'INSULATION_RESISTANCE',
    dataType: DataType.U16,
    description: 'Insulation Resistance',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3DeviceWorkMode: Partial<RegisterTable> = {
  AddressMask_Config_Remote1: {
    address: 0x1100,
    name: 'AddressMask_Config_Remote1',
    dataType: DataType.U64,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'AddressMask_Config_Remote1',
    quantity: 4,
    isMask: true,
  },
  WORK_MODE: {
    address: 0x1110,
    name: 'ENERGY_STORAGE_MODE_CONTROL',
    dataType: DataType.U16,
    description: `
    Energy storage working mode setting;
    0: Spontaneous self-use mode
    1: Time-of-use electricity price model
    2: Timing charging and discharging mode
    3: Passive mode
    4: Peak clipping mode
    5: Off-grid mode
    6：Generator mode
    Used to change the working mode.`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'select',
      labelKey: 'energyStorageModeControl',
      settingsCode: 'EnergyStorageModeControl',
      options: [
        { value: 0, label: 'spontaneousSelfUseMode' },
        { value: 1, label: 'timeOfUseElectricityPriceModel' },
        { value: 2, label: 'timingChargingAndDischargingMode' },
        { value: 3, label: 'passiveMode' },
        { value: 4, label: 'peakClippingMode' },
        { value: 5, label: 'offGridMode' },
        { value: 6, label: 'generatorMode' },
      ],
    },
  },
};

export const V3DeviceWorkModeTiming: Partial<RegisterTable> = {
  TIMING_ID: {
    address: 0x1111,
    name: 'TIMING_ID',
    dataType: DataType.U16,
    description: 'Timing ID',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'select',
      labelKey: 'timingID',
      settingsCode: 'TimingId',
      options: [
        { value: 0, label: 'COMMON_RULE' },
        { value: 1, label: 'COMMON_RULE' },
        { value: 2, label: 'COMMON_RULE' },
        { value: 3, label: 'COMMON_RULE' },
      ],
    },
  },
  TIMING_OFF_ON_CONTROL: {
    address: 0x1112,
    name: 'TIMING_OFF_ON_CONTROL',
    dataType: DataType.U16,
    description: 'Timing Off On Control',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'select',
      labelKey: 'timingOffOnControl',
      settingsCode: 'TimingOffOnControl',
      options: [
        { value: 0, label: 'disabledDischargingAndCharging' },
        { value: 1, label: 'enabledOnlyCharging' },
        { value: 2, label: 'enabledOnlyDischarging' },
        { value: 3, label: 'enabledDischargingAndCharging' },
      ],
    },
  },
  TIMING_CHARGE_START: {
    address: 0x1113,
    name: 'TIMING_CHARGE_START',
    dataType: DataType.U16,
    description: 'Timing Charge Start',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'timingChargeStart',
      settingsCode: 'TimingChargeStart',
      transform: 'time',
    },
  },
  TIMING_CHARGE_END: {
    address: 0x1114,
    name: 'TIMING_CHARGE_END',
    dataType: DataType.U16,
    description: 'Timing Charge End',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'timingChargeEnd',
      settingsCode: 'TimingChargeEnd',
      transform: 'time',
    },
  },
  TIMING_DISCHARGE_START: {
    address: 0x1115,
    name: 'TIMING_DISCHARGE_START',
    dataType: DataType.U16,
    description: 'Timing Discharge Start',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'timingDischargeStart',
      settingsCode: 'TimingDischargeStart',
      transform: 'time',
    },
  },
  TIMING_DISCHARGE_END: {
    address: 0x1116,
    name: 'TIMING_DISCHARGE_END',
    dataType: DataType.U16,
    description: 'Timing Discharge End',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'timingDischargeEnd',
      settingsCode: 'TimingDischargeEnd',
      transform: 'time',
    },
  },
  TIMING_CHARGE_POWER: {
    address: 0x1117,
    name: 'TIMING_CHARGE_POWER',
    dataType: DataType.U32,
    description: 'Timing Charge Power',
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'timingChargePower',
      settingsCode: 'TimingChargePower',
      displayUnit: 'W',
      constraint: { min: 0, max: 4294967295 },
    },
  },
  TIMING_DISCHARGE_POWER: {
    address: 0x1119,
    name: 'TIMING_DISCHARGE_POWER',
    dataType: DataType.U32,
    description: 'Timing Discharge Power',
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'timingDischargePower',
      settingsCode: 'TimingDischargePower',
      displayUnit: 'W',
      constraint: { min: 0, max: 4294967295 },
    },
  },
  TIMING_RSVD1: {
    address: 0x111B,
    name: 'TIMING_RSVD1',
    dataType: DataType.U16,
    description: 'Timing Rsvd1',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  TIMING_RSVD2: {
    address: 0x111C,
    name: 'TIMING_RSVD2',
    dataType: DataType.U16,
    description: 'Timing Rsvd2',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  TIMING_RSVD3: {
    address: 0x111D,
    name: 'TIMING_RSVD3',
    dataType: DataType.U16,
    description: 'Timing Rsvd3',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  TIMING_RSVD4: {
    address: 0x111E,
    name: 'TIMING_RSVD4',
    dataType: DataType.U16,
    description: 'Timing Rsvd4',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  TIMING_CONTROL: {
    address: 0x111F,
    name: 'TIMING_CONTROL',
    dataType: DataType.U16,
    description: 'Timing Control',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceWorkModeTOU: Partial<RegisterTable> = {
  TOU_ID: {
    address: 0x1120,
    name: 'TOU_ID',
    dataType: DataType.U16,
    description: 'Time-of-use electricity price-rule number',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'select',
      labelKey: 'timeOfUseElectricityPriceRuleNumber',
      settingsCode: 'TouId',
      options: [
        { value: 0, label: 'COMMON_RULE' },
        { value: 1, label: 'COMMON_RULE' },
        { value: 2, label: 'COMMON_RULE' },
        { value: 3, label: 'COMMON_RULE' },
      ],
    },
  },
  TOU_On_Off_Control: {
    address: 0x1121,
    name: 'TOU_On_Off_Control',
    dataType: DataType.U16,
    description: 'Time-of-use price-rule enable',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'select',
      labelKey: 'timeOfUsePriceRuleEnable',
      settingsCode: 'TouOnOffControl',
      options: [
        { value: 0, label: 'Off' },
        { value: 1, label: 'On' },
      ],
    },
  },
  TOU_Charge_Start: {
    address: 0x1122,
    name: 'TOU_Charge_Start',
    dataType: DataType.U16,
    description: 'Charging start hour and minute',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'chargingStartHourAndMinute',
      settingsCode: 'TouChargeStart',
      transform: 'time',
    },
  },
  TOU_Charge_End: {
    address: 0x1123,
    name: 'TOU_Charge_End',
    dataType: DataType.U16,
    description: 'Charging end hour and minute',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'time',
      labelKey: 'chargingEndHourAndMinute',
      settingsCode: 'TouChargeEnd',
      transform: 'time',
    },
  },
  TOU_Charge_Target_SOC: {
    address: 0x1124,
    name: 'TOU_Charge_Target_SOC',
    dataType: DataType.U16,
    description: 'Time-of-use price-mandatory charging cut-off SOC',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'timeOfUsePriceMandatoryChargingCutOffSOC',
      settingsCode: 'TouChargeTargetSoc',
      displayUnit: '%',
      constraint: { min: 10, max: 100 },
    },
  },
  TOU_Charge_Power: {
    address: 0x1125,
    name: 'TOU_Charge_Power',
    dataType: DataType.U32,
    description: 'Time-of-use price-mandatory charging power',
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'timeOfUsePriceMandatoryChargingPower',
      settingsCode: 'TouChargePower',
      displayUnit: 'W',
      constraint: { min: 0, max: 4294967295 },
    },
  },
  TOU_Executed_Date_Start: {
    address: 0x1127,
    name: 'TOU_Executed_Date_Start',
    dataType: DataType.U16,
    description: 'The month and date when the rule becomes effective',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'date',
      labelKey: 'theMonthAndDateWhenTheRuleBecomesEffective',
      settingsCode: 'TouExecutedDateStart',
      transform: 'date',
    },
  },
  TOU_Executed_Date_End: {
    address: 0x1128,
    name: 'TOU_Executed_Date_End',
    dataType: DataType.U16,
    description: 'The month and date when the rule ceases to take effect',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'date',
      labelKey: 'theMonthAndDateWhenTheRuleCeasesToTakeEffect',
      settingsCode: 'TouExecutedDateEnd',
      transform: 'date',
    },
  },
  TOU_Executed_Day_of_Week: {
    address: 0x1129,
    name: 'TOU_Executed_Day_of_Week',
    dataType: DataType.U16,
    description: 'Time-of-use electricity price-week when the rule takes effect',
    access: ReadWrite.ReadWrite,
    quantity: 1,
    ui: {
      fieldType: 'weekday',
      labelKey: 'timeOfUseElectricityPriceWeekWhenTheRuleTakesEffect',
      settingsCode: 'TouExecutedDayOfWeek',
      transform: 'weekday',
    },
  },
  TOU_Control: {
    address: 0x112F,
    name: 'TOU_Control',
    dataType: DataType.U16,
    description: `
    Time-of-use energy price-write control;
    When the written value is 1,
      the value in the shadow register of the time-of-use electricity price is updated
      to the system time-of-use electricity price configuration;
    When reading, return the status of the last write operation:
    0x0000: success
    0x0001: operating
    0xFFFB: The operation failed and the controller refused to respond (maybe the controller is busy or the configuration is wrong)
    0xFFFC: The operation failed and the controller did not respond
    0xFFFD: Operation failed, current function is forbidden
    0xFFFE: Operation failed, parameter storage failed
    0xFFFF: The operation failed, and the input parameters are incorrect`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceWorkModePassive: Partial<RegisterTable> = {
  Passive_Manual_Gdes: {
    address: 0x1187,
    name: 'Passive_Manual_Gdes',
    dataType: DataType.I32,
    unit: 'W',
    description: `
    Manual mode expected grid power (Gdes);
    A positive value indicates the power direction "from the grid to the system";
    Negative values indicate the power direction "from system to grid".`,
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'desireGridPower',
      settingsCode: 'PassiveManualGdes',
      displayUnit: 'W',
      constraint: { min: -2147483648, max: 2147483647 },
    },
  },
  Passive_Manual_Blo: {
    address: 0x1189,
    name: 'Passive_Manual_Blo',
    dataType: DataType.I32,
    unit: 'W',
    description:`
    The minimum charge and discharge power of the battery in Manual mode (Blo);
    Positive value means charging;
    Negative values indicate discharge.`,
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'batteryPowerLowerLimit',
      settingsCode: 'PassiveManualBlo',
      displayUnit: 'W',
      constraint: { min: -2147483648, max: 2147483647 },
    },
  },
  Passive_Manual_Bup: {
    address: 0x118B,
    name: 'Passive_Manual_Bup',
    dataType: DataType.I32,
    unit: 'W',
    description:`
    The maximum charge and discharge power of the battery in Manual mode (Bup);
    Positive value means charging;
    Negative values indicate discharge.`,
    access: ReadWrite.ReadWrite,
    quantity: 2,
    ui: {
      fieldType: 'input',
      inputType: 'number',
      labelKey: 'batteryPowerUpperLimit',
      settingsCode: 'PassiveManualBup',
      displayUnit: 'W',
      constraint: { min: -2147483648, max: 2147483647 },
    },
  },
};

export const V3DevicePCCConfig: Partial<RegisterTable> = {
  PCCSampleMode: {
    address: 0x1060,
    name: 'PCCSampleMode',
    dataType: DataType.U16,
    description: `
    PCC power acquisition device configuration
    0: not used
    1: Electricity meter
    2: CT
    3: ARPC`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DeviceWorkModeConfig: Partial<RegisterTable> = {
  ...V3DeviceWorkMode,
  ...V3DeviceWorkModeTiming,
  ...V3DeviceWorkModeTOU,
  ...V3DeviceWorkModePassive,
  ...V3DevicePCCConfig,
};

export const V3LogicalInterface: Partial<RegisterTable> = {
  LogicInterface_Control: {
    address: 0x103e,
    name: 'LogicInterface_Control',
    dataType: DataType.U16,
    description: `
    Logical Interface Control Register
    0: Disable
    1: enable`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3DCI: Partial<RegisterTable> = {
  DCI_R: {
    address: 0x06c6,
    name: 'DCI_R',
    dataType: DataType.I16,
    unit: 'mA',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'R-phase current DC component',
    quantity: 1,
  },
  DCI_S: {
    address: 0x06c7,
    name: 'DCI_S',
    dataType: DataType.I16,
    unit: 'mA',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'S-phase current DC component',
    quantity: 1,
  },
  DCI_T: {
    address: 0x06c8,
    name: 'DCI_T',
    dataType: DataType.I16,
    unit: 'mA',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'T-phase current DC component',
    quantity: 1,
  },
};

export const V3VoltageBus: Partial<RegisterTable> = {
  Voltage_Bus_LLC: {
    address: 0x06cf,
    name: 'Voltage_Bus_LLC',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'LLC Bus voltage',
    quantity: 1,
  },
  Voltage_Bus_P_Half: {
    address: 0x06d1,
    name: 'Voltage_Bus_P_Half',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'BUS positive half voltage',
    quantity: 1,
  },
  Voltage_Bus_N_Half: {
    address: 0x06d2,
    name: 'Voltage_Bus_N_Half',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'BUS negative half voltage',
    quantity: 1,
  },
  FlyingCap_Voltage1: {
    address: 0x06d3,
    name: 'FlyingCap_Voltage1',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'PV1 flying capacitor voltage',
    quantity: 1,
  },
};

export const V3CurrentBuckBoost: Partial<RegisterTable> = {
  Current_BuckBoost: {
    address: 0x06d0,
    name: 'Current_BuckBoost',
    dataType: DataType.I16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'BuckBoost current',
    quantity: 1,
  },
};

export const ComposeV3DeviceLogicalInterface: Partial<InverterDataComposer> = {
  LogicInterface_Control: (data: Partial<InverterData>) => {
    const value = data.LogicInterface_Control;
    if (value === undefined || typeof value !== 'number') {
      data.LogicInterface_Control = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) {
      data.LogicInterface_Control = modbusServicesLabels['Common.disabled'];
    } else if (value === 1) {
      data.LogicInterface_Control = modbusServicesLabels['Common.enabled'];
    }
    return data;
  },
};

export const ComposeV3DeviceInputTypeChannelConfig: Partial<InverterDataComposer> = {};
for (let i = 0; i <= V3InputTypeChannelConfig.Input_Type.length; i++) {
  ComposeV3DeviceInputTypeChannelConfig[`INPUT_TYPE_CHANNEL_CONFIG${i}`] = (data: Partial<InverterData>) => {

    const value = data[`INPUT_TYPE_CHANNEL_CONFIG${i}`];
    if (value === undefined || typeof value !== 'number') {
      data[`INPUT_TYPE_CHANNEL_CONFIG_STRING${i}`] = undefined;
      return data;
    }
    if (value === 0) {
      data[`INPUT_TYPE_CHANNEL_CONFIG_STRING${i}`] = undefined;
    } else if (typeof value === 'number' && value > 0 && value <= 127) {
      data[`INPUT_TYPE_CHANNEL_CONFIG_STRING${i}`] = modbusServicesLabels['DeviceInfoPage.pv'].replace('{{value}}', `${value}`);
    } else if (typeof value === 'number' && value > 127 && value <= 255) {
      data[`INPUT_TYPE_CHANNEL_CONFIG_STRING${i}`] = modbusServicesLabels['DeviceInfoPage.battery'].replace('{{value}}', `${value - 127}`);
    } else {
      data[`INPUT_TYPE_CHANNEL_CONFIG_STRING${i}`] = modbusServicesLabels['DeviceInfoPage.pvAndBattery'];
    }
    return data;
  };
}

export const ComposeV3DeviceCountryCode: Partial<InverterDataComposer> = {
  COUNTRY: (data: Partial<InverterData>) => {

    const value = data.COUNTRY;
    if (value === undefined || typeof value !== 'number') {
      data.COUNTRY = modbusServicesLabels['DeviceInfoPage.notAvailable'];
      return data;
    }
    data.COUNTRY = getCountryNameFromCodeV3(value);
    return data;
  },
};

export const ComposeV3DeviceInputModeConfig: Partial<InverterDataComposer> = {
  INPUT_MODE_CONFIG: (data: Partial<InverterData>) => {

    const value = data.INPUT_MODE_CONFIG;
    if (value === undefined || typeof value !== 'number') {
      data.INPUT_MODE_CONFIG = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Parallel Mode
      data.INPUT_MODE_CONFIG = modbusServicesLabels['DeviceInfoPage.parallelMode'];
    } else if (value === 1) { // 1: Independent Mode
      data.INPUT_MODE_CONFIG = modbusServicesLabels['DeviceInfoPage.independentMode'];
    }

    return data;
  },
  EPS_CONTROL: (data: Partial<InverterData>) => {

    const value = data.EPS_CONTROL;
    if (value === undefined || typeof value !== 'number') {
      data.EPS_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Disable emergency power supply
      data.EPS_CONTROL = modbusServicesLabels['DeviceInfoPage.disableEmergencyPowerSupply'];
    } else if (value === 1) { // 1 Enable emergency power supply ,disable cold start
      data.EPS_CONTROL = modbusServicesLabels['DeviceInfoPage.enableEmergencyPowerSupplyDisableColdStart'];
    } else if (value === 2) { // 2 Enable emergency power supply enable cold start
      data.EPS_CONTROL = modbusServicesLabels['DeviceInfoPage.enableEmergencyPowerSupplyEnableColdStart'];
    }

    return data;
  },
  EPS_WAIT_TIME: (data: Partial<InverterData>) => {
    const value = data.EPS_WAIT_TIME;
    if (value === undefined || typeof value !== 'number') {
      data.EPS_WAIT_TIME = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    }
    const isDisabled = data.EPS_CONTROL === modbusServicesLabels['DeviceInfoPage.disableEmergencyPowerSupply'] ||
      data.EPS_CONTROL === 0;
    data.EPS_WAIT_TIME = `${value} ${modbusServicesLabels['DeviceInfoPage.seconds']} ${isDisabled
      ? `(${modbusServicesLabels['DeviceInfoPage.disabled']})` : ''}`;
    return data;
  },
  SCAN_CURVE_IV_CONTROL: (data: Partial<InverterData>) => {
    const value = data.SCAN_CURVE_IV_CONTROL;
    if (value === undefined || typeof value !== 'number') {
      data.SCAN_CURVE_IV_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) {
      data.SCAN_CURVE_IV_CONTROL = modbusServicesLabels['DeviceInfoPage.disableScanCurve'];
    } else if (value === 1) {
      data.SCAN_CURVE_IV_CONTROL = modbusServicesLabels['DeviceInfoPage.enableScanCurve'];
    }
    return data;
  },
  SCAN_CURVE_IV_PERIOD: (data: Partial<InverterData>) => {
    const value = data.SCAN_CURVE_IV_PERIOD;
    if (value === undefined || typeof value !== 'number') {
      data.SCAN_CURVE_IV_PERIOD = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    }
    const isDisabled = data.SCAN_CURVE_IV_CONTROL === modbusServicesLabels['DeviceInfoPage.disableScanCurve'] ||
      data.SCAN_CURVE_IV_CONTROL === 0;
    data.SCAN_CURVE_IV_PERIOD = `${value} ${modbusServicesLabels['DeviceInfoPage.minutes']} ${isDisabled
      ? `(${modbusServicesLabels['DeviceInfoPage.disabled']})` : ''}`;
    return data;
  },
  ANTI_REFLUX_CONTROL: (data: Partial<InverterData>) => {
    const value = data.ANTI_REFLUX_CONTROL;
    if (value === undefined || typeof value !== 'number') {
      data.ANTI_REFLUX_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) {
      data.ANTI_REFLUX_CONTROL = modbusServicesLabels['DeviceInfoPage.disableAntiReflux'];
    } else if (value === 1) {
      data.ANTI_REFLUX_CONTROL = modbusServicesLabels['DeviceInfoPage.enableAntiRefluxMinPower'];
    } else if (value === 2) {
      data.ANTI_REFLUX_CONTROL = modbusServicesLabels['DeviceInfoPage.enableAntiRefluxMeanValue'];
    }
    return data;
  },
  ANTI_REFLUX_POWER: (data: Partial<InverterData>) => {
    const value = data.ANTI_REFLUX_POWER;
    if (value === undefined || typeof value !== 'number') {
        data.ANTI_REFLUX_POWER = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    }
    const isDisabled = data.ANTI_REFLUX_CONTROL === modbusServicesLabels['DeviceInfoPage.disableAntiReflux'] ||
      data.ANTI_REFLUX_CONTROL === 0;
    data.ANTI_REFLUX_POWER = `${(Number(value) / 1000).toFixed(1)} kW ${isDisabled
      ? `(${modbusServicesLabels['DeviceInfoPage.disabled']})` : ''}`;
    return data;
  },
  PARALLEL_CONTROL: (data: Partial<InverterData>) => {
    const value = data.PARALLEL_CONTROL;
    if (value === undefined || typeof value !== 'number') {
        data.PARALLEL_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Disable parallel
        data.PARALLEL_CONTROL = modbusServicesLabels['Common.disabled'];
    } else if (value === 1) { // 1: Enable parallel
        data.PARALLEL_CONTROL = modbusServicesLabels['Common.enabled'];
    }
    return data;
  },
  PARALLEL_MASTER_SLAVE: (data: Partial<InverterData>) => {
    const value = data.PARALLEL_MASTER_SLAVE;
    if (value === undefined || typeof value !== 'number') {
        data.PARALLEL_MASTER_SLAVE = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Slave
        data.PARALLEL_MASTER_SLAVE = modbusServicesLabels['DeviceInfoPage.slave'];
    } else if (value === 1) { // 1: Master
        data.PARALLEL_MASTER_SLAVE = modbusServicesLabels['DeviceInfoPage.master'];
    }
    return data;
  },
  PARALLEL_ADDRESS: (data: Partial<InverterData>) => {
    const value = data.PARALLEL_ADDRESS;
    if (value === undefined || typeof value !== 'number') {
        data.PARALLEL_ADDRESS = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    }
    const isDisabled = data.PARALLEL_CONTROL === modbusServicesLabels['Common.disabled'] || data.PARALLEL_CONTROL === 0;
    data.PARALLEL_ADDRESS = `${value} ${isDisabled ? `(${modbusServicesLabels['DeviceInfoPage.disabled']})` : ''}`;
    return data;
  },
  UNBALANCED_SUPPORT_CONTROL: (data: Partial<InverterData>) => {
    const value = data.UNBALANCED_SUPPORT_CONTROL;
    if (value === undefined || typeof value !== 'number') {
        data.UNBALANCED_SUPPORT_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Disable unbalanced support
        data.UNBALANCED_SUPPORT_CONTROL = modbusServicesLabels['DeviceInfoPage.disableUnbalancedSupport'];
    } else if (value === 1) { // 1: Enable unbalanced support
        data.UNBALANCED_SUPPORT_CONTROL = modbusServicesLabels['DeviceInfoPage.enableUnbalancedSupport'];
    }
    return data;
  },
  BATTERY_ACTIVE_CONTROL: (data: Partial<InverterData>) => {
    const value = data.BATTERY_ACTIVE_CONTROL;
    if (value === undefined || typeof value !== 'number') {
        data.BATTERY_ACTIVE_CONTROL = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Disable battery active
        data.BATTERY_ACTIVE_CONTROL = modbusServicesLabels['DeviceInfoPage.disableBatteryActive'];
    } else if (value === 1) { // 1: Enable battery active
        data.BATTERY_ACTIVE_CONTROL = modbusServicesLabels['DeviceInfoPage.enableBatteryActive'];
    }
    return data;
  },
  BATTERY_ACTIVE_ONESHOT: (data: Partial<InverterData>) => {
    const value = data.BATTERY_ACTIVE_ONESHOT;
    if (value === undefined || typeof value !== 'number') {
        data.BATTERY_ACTIVE_ONESHOT = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else if (value === 0) { // 0: Disable battery active one shot
        data.BATTERY_ACTIVE_ONESHOT = modbusServicesLabels['DeviceInfoPage.disableBatteryActiveOneShot'];
    } else if (value === 1) { // 1: Enable battery active one shot
        data.BATTERY_ACTIVE_ONESHOT = modbusServicesLabels['DeviceInfoPage.enableBatteryActiveOneShot'];
    }
    return data;
  },
};

export const ComposeV3DeviceLogicalInterfaces: Partial<InverterDataComposer> = {
  REMOTE_CONFIG: (data: Partial<InverterData>) => {
    const value = data.REMOTE_CONFIG;
    if (value === undefined || typeof value !== 'number') {
      data.REMOTE_CONFIG = `Not Available`;
    } else {
      const bits = value.toString(2).padStart(16, '0').split('').reverse();
      const bitsDescription = [
        modbusServicesLabels['DeviceInfoPage.activePowerDerate'],
        modbusServicesLabels['DeviceInfoPage.remotelyOnOffControl'],
        modbusServicesLabels['DeviceInfoPage.powerDerateLeadingByHighACVoltage'],
        modbusServicesLabels['DeviceInfoPage.batteryChargingPowerDerateLeadingHighVoltage'],
        modbusServicesLabels['DeviceInfoPage.batteryChargingPowerDerateByLowVoltage'],
        modbusServicesLabels['report.DRM0'],
        modbusServicesLabels['DeviceInfoPage.logicInterfaceDrm1-8'],
        modbusServicesLabels['DeviceInfoPage.antiBackflowOverloadControl'],
        modbusServicesLabels['DeviceInfoPage.voltageDropoutPTCurveControl'],
      ];
      const enabled = modbusServicesLabels['Common.enabled'];
      const disabled = modbusServicesLabels['Common.disabled'];

      data.REMOTE_CONFIG = {
        type: CustomInverterType.Object,
        value: {
          [bitsDescription[0]]: bits[0] === '1' ? enabled : disabled,
          [bitsDescription[1]]: bits[1] === '1' ? enabled : disabled,
          [bitsDescription[2]]: bits[2] === '1' ? enabled : disabled,
          [bitsDescription[3]]: bits[3] === '1' ? enabled : disabled,
          [bitsDescription[4]]: bits[4] === '1' ? enabled : disabled,
          [bitsDescription[5]]: bits[5] === '1' ? enabled : disabled,
          [bitsDescription[6]]: bits[6] === '1' ? enabled : disabled,
          [bitsDescription[7]]: bits[7] === '1' ? enabled : disabled,
          [bitsDescription[8]]: bits[8] === '1' ? enabled : disabled,
        },
      };
    }
    return data;
  },
};

export const ComposeV3DeviceResistanceConfig: Partial<InverterDataComposer> = {
  ISO_CONFIG: (data: Partial<InverterData>) => {
    const value = data.ISO_CONFIG;
    if (value === undefined || typeof value !== 'number') {
      data.ISO_CONFIG = modbusServicesLabels['DeviceInfoPage.notAvailable'];
    } else {
      const bits = value.toString(2).padStart(16, '0').split('').reverse();

      const bitsDescription = [
        modbusServicesLabels['DeviceInfoPage.insulationResistanceTestingEnableBit'],
        modbusServicesLabels['DeviceInfoPage.groundTestingEnableBit'],
      ];

      const enabled = modbusServicesLabels['Common.enabled'];
      const disabled = modbusServicesLabels['Common.disabled'];

      data.ISO_CONFIG = {
        type: CustomInverterType.Object,
        value: {
          [bitsDescription[0]]: bits[0] === '1' ? enabled : disabled,
          [bitsDescription[1]]: bits[1] === '1' ? enabled : disabled,
        },
      };
    }

    return data;
  },
  INSULATION_RESISTANCE: (data: Partial<InverterData>) => {
    const value = data.INSULATION_RESISTANCE;
    if (value === undefined || typeof value !== 'number') {
      data.INSULATION_RESISTANCE = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    }
    data.INSULATION_RESISTANCE = `${value} kOhm`;

    return data;
  },
};

export const ComposeV3DeviceBatteryConfig: Partial<InverterDataComposer> = {
  BatConfig_Protocol: (data: Partial<InverterData>) => {
    const value = data.BatConfig_Protocol;
    if (value === undefined || typeof value !== 'number') {
      data.BatConfig_Protocol = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * Battery parameter-communication protocol
       * 0: Sofar Built-in BMS
       * 1: Pylontech
       * 2: Weco LV
       * 3: ZSX Azzurro
       * 4: LG
       * 5: Alpha ESS
       * 6: CATL
       * 7: Weco HV
       * 11: BTS 5K
       */

      switch (value) {
        case 0:
          data.BatConfig_Protocol = `Sofar Built-in BMS`;
          break;
        case 1:
          data.BatConfig_Protocol = `Pylontech`;
          break;
        case 2:
          data.BatConfig_Protocol = `Weco LV`;
          break;
        case 3:
          data.BatConfig_Protocol = `ZSX Azzurro`;
          break;
        case 4:
          data.BatConfig_Protocol = `LG`;
          break;
        case 5:
          data.BatConfig_Protocol = `Alpha ESS`;
          break;
        case 6:
          data.BatConfig_Protocol = `CATL`;
          break;
        case 7:
          data.BatConfig_Protocol = `Weco HV`;
          break;
        case 11:
          data.BatConfig_Protocol = `BTS 5K`;
          break;
        default:
          data.BatConfig_Protocol = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
          break;
      };
    }

    return data;
  },
  BatConfig_Cell_Type: (data: Partial<InverterData>) => {
    const value = data.BatConfig_Cell_Type;
    if (value === undefined || typeof value !== 'number') {
      data.BatConfig_Cell_Type = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * Battery parameter-Battery Type
       * 0: Lead-acid(Default)
       * 1: lithium iron phosphate Battery
       * 2: Lithium Ternary Battery
       * 3: lithium titanate Battery
       * 4：AGM
       * 5：Gel
       * 6：Flooded
       */

      switch (value) {
        case 0:
          data.BatConfig_Cell_Type = `Lead-acid`;
          break;
        case 1:
          data.BatConfig_Cell_Type = `lithium iron phosphate Battery`;
          break;
        case 2:
          data.BatConfig_Cell_Type = `Lithium Ternary Battery`;
          break;
        case 3:
          data.BatConfig_Cell_Type = `lithium titanate Battery`;
          break;
        case 4:
          data.BatConfig_Cell_Type = `AGM`;
          break;
        case 5:
          data.BatConfig_Cell_Type = `Gel`;
          break;
        case 6:
          data.BatConfig_Cell_Type = `Flooded`;
          break;
        default:
          data.BatConfig_Cell_Type = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
          break;
      };
    }

    return data;
  },
};

export const ComposeV3DeviceWorkModeConfig: Partial<InverterDataComposer> = {
  WORK_MODE: (data: Partial<InverterData>) => {
    const value = data.WORK_MODE;
    if (value === undefined || typeof value !== 'number') {
      data.WORK_MODE = modbusServicesLabels['WorkMode.notAvailable'];
    } else if (value === 0) { // 0: Spontaneous self-use mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.spontaneousSelfUseMode'];
    } else if (value === 1) { // 1: Time-of-use electricity price model
      data.WORK_MODE = modbusServicesLabels['WorkMode.timeOfUseElectricityPriceModel'];
    } else if (value === 2) { // 2: Timing charging and discharging mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.timingChargingAndDischargingMode'];
    } else if (value === 3) { // 3: Passive mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.passiveMode'];
    } else if (value === 4) { // 4: Peak clipping mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.peakClippingMode'];
    } else if (value === 5) { // 5: Off-grid mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.offGridMode'];
    } else if (value === 6) { // 6：Generator mode
      data.WORK_MODE = modbusServicesLabels['WorkMode.generatorMode'];
    }

    return data;
  },
  TIMING_OFF_ON_CONTROL: (data: Partial<InverterData>) => {
    const value = data.TIMING_OFF_ON_CONTROL;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_OFF_ON_CONTROL = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      const bits = value.toString(2).padStart(16, '0').split('').reverse();
      const bitsDescription = [
        modbusServicesLabels['DeviceInfoPage.chargeEnable'],
        modbusServicesLabels['DeviceInfoPage.dischargeEnable'],
      ];

      const enabled = modbusServicesLabels['Common.enabled'];
      const disabled = modbusServicesLabels['Common.disabled'];

      data.TIMING_OFF_ON_CONTROL = {
        type: CustomInverterType.Object,
        value: {
          [bitsDescription[0]]: bits[0] === '1' ? enabled : disabled,
          [bitsDescription[1]]: bits[1] === '1' ? enabled : disabled,
        },
      };
    }

    return data;
  },
  TIMING_CHARGE_START: (data: Partial<InverterData>) => {
    const value = data.TIMING_CHARGE_START;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_CHARGE_START = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Charging start hour
       * Low byte. Charging start minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TIMING_CHARGE_START = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TIMING_CHARGE_END: (data: Partial<InverterData>) => {
    const value = data.TIMING_CHARGE_END;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_CHARGE_END = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Charging end hour
       * Low byte. Charging end minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TIMING_CHARGE_END = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TIMING_DISCHARGE_START: (data: Partial<InverterData>) => {
    const value = data.TIMING_DISCHARGE_START;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_DISCHARGE_START = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Discharging start hour
       * Low byte. Discharging start minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TIMING_DISCHARGE_START = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TIMING_DISCHARGE_END: (data: Partial<InverterData>) => {
    const value = data.TIMING_DISCHARGE_END;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_DISCHARGE_END = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Discharging end hour
       * Low byte. Discharging end minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TIMING_DISCHARGE_END = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TIMING_CHARGE_POWER: (data: Partial<InverterData>) => {
    const value = data.TIMING_CHARGE_POWER;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_CHARGE_POWER = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.TIMING_CHARGE_POWER = `${value} W`;
    }

    return data;
  },
  TIMING_DISCHARGE_POWER: (data: Partial<InverterData>) => {
    const value = data.TIMING_DISCHARGE_POWER;
    if (value === undefined || typeof value !== 'number') {
      data.TIMING_DISCHARGE_POWER = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.TIMING_DISCHARGE_POWER = `${value} W`;
    }

    return data;
  },
  TOU_ID: (data: Partial<InverterData>) => {
    const value = data.TOU_ID;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_ID = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.TOU_ID = `${value}`;
    }
    return data;
  },
  TOU_On_Off_Control: (data: Partial<InverterData>) => {
    const value = data.TOU_On_Off_Control;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_On_Off_Control = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else if (value === 0) { // 0: disabled
      data.TOU_On_Off_Control = `${modbusServicesLabels['Common.disabled']}`;
    } else if (value === 1) { // 1: enabled
      data.TOU_On_Off_Control = `${modbusServicesLabels['Common.enabled']}`;
    }
    return data;
  },
  TOU_Charge_Start: (data: Partial<InverterData>) => {
    const value = data.TOU_Charge_Start;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Charge_Start = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Charging start hour
       * Low byte. Charging start minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TOU_Charge_Start = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TOU_Charge_End: (data: Partial<InverterData>) => {
    const value = data.TOU_Charge_End;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Charge_End = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      /**
       * High byte. Charging start hour
       * Low byte. Charging start minutes
       */
      const hour = Math.floor(value / 256);
      const minute = value % 256;
      data.TOU_Charge_End = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TOU_Charge_Target_SOC: (data: Partial<InverterData>) => {
    const value = data.TOU_Charge_Target_SOC;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Charge_Target_SOC = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.TOU_Charge_Target_SOC = `${value}%`;
    }
    return data;
  },
  TOU_Charge_Power: (data: Partial<InverterData>) => {
    const value = data.TOU_Charge_Power;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Charge_Power = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.TOU_Charge_Power = `${value} W`;
    }
    return data;
  },
  TOU_Executed_Date_Start: (data: Partial<InverterData>) => {
    const value = data.TOU_Executed_Date_Start;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Executed_Date_Start = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      const month = Math.floor(value / 256);
      const date = value % 256;
      data.TOU_Executed_Date_Start = `${month.toString().padStart(2, '0')}/${date.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TOU_Executed_Date_End: (data: Partial<InverterData>) => {
    const value = data.TOU_Executed_Date_End;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Executed_Date_End = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      const month = Math.floor(value / 256);
      const date = value % 256;
      data.TOU_Executed_Date_End = `${month.toString().padStart(2, '0')}/${date.toString().padStart(2, '0')}`;
    }

    return data;
  },
  TOU_Executed_Day_of_Week: (data: Partial<InverterData>) => {
    const value = data.TOU_Executed_Day_of_Week;
    if (value === undefined || typeof value !== 'number') {
      data.TOU_Executed_Day_of_Week = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      const bits = value.toString(2).padStart(16, '0').split('').reverse();
      data.TOU_Executed_Day_of_Week = {
        type: CustomInverterType.Object,
        value: {
          [modbusServicesLabels['Common.monday']]: bits[0] === '1',
          [modbusServicesLabels['Common.tuesday']]: bits[1] === '1',
          [modbusServicesLabels['Common.wednesday']]: bits[2] === '1',
          [modbusServicesLabels['Common.thursday']]: bits[3] === '1',
          [modbusServicesLabels['Common.friday']]: bits[4] === '1',
          [modbusServicesLabels['Common.saturday']]: bits[5] === '1',
          [modbusServicesLabels['Common.sunday']]: bits[6] === '1',
        },
      };
    }

    return data;
  },
  Passive_Manual_Blo: (data: Partial<InverterData>) => {
    const value = data.Passive_Manual_Blo;
    if (value === undefined || typeof value !== 'number') {
      data.Passive_Manual_Blo = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.Passive_Manual_Blo = `${value} W`;
    }
    return data;
  },
  Passive_Manual_Bup: (data: Partial<InverterData>) => {
    const value = data.Passive_Manual_Bup;
    if (value === undefined || typeof value !== 'number') {
      data.Passive_Manual_Bup = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.Passive_Manual_Bup = `${value} W`;
    }
    return data;
  },
  Passive_Manual_Gdes: (data: Partial<InverterData>) => {
    const value = data.Passive_Manual_Gdes;
    if (value === undefined || typeof value !== 'number') {
      data.Passive_Manual_Gdes = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      data.Passive_Manual_Gdes = `${value} W`;
    }
    return data;
  },
  PCCSampleMode: (data: Partial<InverterData>) => {
    const value = data.PCCSampleMode;
    if (value === undefined || typeof value !== 'number') {
      data.PCCSampleMode = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
    } else {
      switch (value) {
        case 0:
          data.PCCSampleMode = `${modbusServicesLabels['Common.notUsed']}`;
          break;
        case 1:
          data.PCCSampleMode = `Electricity meter`;
          break;
        case 2:
          data.PCCSampleMode = `CT`;
          break;
        case 3:
          data.PCCSampleMode = `ARPC`;
          break;
        default:
          data.PCCSampleMode = `${modbusServicesLabels['DeviceInfoPage.notAvailable']}`;
          break;
      };
    }
    return data;
  },
};
